<script setup>
import { ref, reactive, computed, watch } from 'vue'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import { CLAUSULAS_COM_APLIC } from '@/constants/CLAUSULAS_COM_APLIC'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  idDocumento: { type: Number, required: true },
  tipoDocumento: { type: String, required: true, validator: (v) => ['PO', 'PV', 'OC'].includes(v) },
  lstAplicacaoClausula: { type: String, required: true },
  clausulaEditando: { type: Object, default: null },
  produtosDisponiveis: { type: Array, default: () => [] },
  parceirosDisponiveis: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:modelValue', 'salva', 'show-notification'])

// ============================================================
// FORMULÁRIO REATIVO
// ============================================================
const form = reactive({
  titulo: '',
  texto: '',
  origem: '',
  idProduto: null,
  idParceiro: null,
  isDefinitiva: false,
  lstVisibilidade: '',
  lstAplicacaoClausula: '',
  atualizarTemplate: false,
  xmin: 0
})

const isSaving = ref(false)

// ============================================================
// TÍTULO DO MODAL
// ============================================================
const tituloModal = computed(() => {
  return props.clausulaEditando ? 'Editar Cláusula' : 'Nova Cláusula'
})

// ============================================================
// OPÇÕES DE ORIGEM POR TIPO DE DOCUMENTO
// ============================================================
const opcoesOrigem = computed(() => {
  const todas = [
    { id: 'Avulsa', label: 'Avulsa — Só será apresentada neste documento' },
    { id: 'Empresa', label: 'Empresa — Cláusula geral da empresa' },
    { id: 'Produto', label: 'Produto — Sempre apresentada quando o produto estiver relacionado' }
  ]

  const isPoOc = props.lstAplicacaoClausula === CLAUSULAS_COM_APLIC.PO ||
                  props.lstAplicacaoClausula === CLAUSULAS_COM_APLIC.OC
  const isPV = props.lstAplicacaoClausula === CLAUSULAS_COM_APLIC.PV

  if (isPoOc) {
    todas.push(
      { id: 'Fornecedor', label: 'Fornecedor — Sempre apresentada para este fornecedor' },
      { id: 'Produto Do Fornecedor', label: 'Produto do Fornecedor — Sempre apresentada quando estiver com o produto relacionado' }
    )
  }

  if (isPV) {
    todas.push(
      { id: 'Cliente', label: 'Cliente — Sempre apresentada para este cliente' },
      { id: 'Produto Do Cliente', label: 'Produto do Cliente — Sempre apresentada quando estiver com o produto relacionado' }
    )
  }

  return todas
})

// ============================================================
// MAPEAMENTO DE ENDPOINTS POR TIPO DE DOCUMENTO
// ============================================================
const endpoints = {
  PO: {
    criar: '/PurchaseOrderClausulas/criar-com-origem',
    atualizarComTemplate: (id) => `/PurchaseOrderClausulas/${id}/atualizar-com-template`,
    atualizarAvulsa: (id) => `/PurchaseOrderClausulas/${id}`
  },
  PV: {
    criar: (_id) => '/PedidoVendaClausulas',
    criarComOrigem: (_id) => '/PedidoVendaClausulas/criar-com-origem',
    atualizarComTemplate: null,
    atualizarAvulsa: (id) => `/PedidoVendaClausulas/${id}`
  },
  OC: {
    criar: (_id) => '/OrdemCompraClausulas/criar-com-origem',
    atualizarComTemplate: null,
    atualizarAvulsa: (id) => `/OrdemCompraClausulas/${id}`
  }
}

const ep = computed(() => endpoints[props.tipoDocumento])

// ============================================================
// INICIALIZAR FORMULÁRIO
// ============================================================
const inicializarForm = () => {
  if (props.clausulaEditando) {
    form.titulo = props.clausulaEditando.titulo || ''
    form.texto = props.clausulaEditando.texto || ''
    form.origem = props.clausulaEditando.origemDescricao || props.clausulaEditando.origem || ''
    form.idProduto = props.clausulaEditando.idProduto || null
    form.idParceiro = props.clausulaEditando.idParceiro || null
    form.isDefinitiva = props.clausulaEditando.isDefinitiva || false
    form.lstVisibilidade = props.clausulaEditando.lstVisibilidade || ''
    form.lstAplicacaoClausula = props.clausulaEditando.lstAplicacaoClausula || props.lstAplicacaoClausula
    form.atualizarTemplate = false
    form.xmin = props.clausulaEditando.xmin || 0
  } else {
    form.titulo = ''
    form.texto = ''
    form.origem = ''
    form.idProduto = null
    form.idParceiro = null
    form.isDefinitiva = false
    form.lstVisibilidade = ''
    form.lstAplicacaoClausula = props.lstAplicacaoClausula
    form.atualizarTemplate = false
    form.xmin = 0
  }
}

// ============================================================
// WATCH: Inicializar formulário quando abrir o modal
// ============================================================
watch(() => props.modelValue, (val) => {
  if (val) {
    inicializarForm()
  }
})

// ============================================================
// VALIDAÇÃO
// ============================================================
const validar = () => {
  if (!form.titulo.trim()) {
    emit('show-notification', { message: 'O Título é obrigatório.', type: 'warning' })
    return false
  }
  if (!form.texto.trim()) {
    emit('show-notification', { message: 'O Texto da cláusula é obrigatório.', type: 'warning' })
    return false
  }
  if (!props.clausulaEditando && !form.origem) {
    emit('show-notification', { message: 'Selecione a origem da cláusula.', type: 'warning' })
    return false
  }
  if ((form.origem === 'Produto' || form.origem === 'Produto Do Fornecedor' || form.origem === 'Produto Do Cliente') && !form.idProduto) {
    emit('show-notification', { message: 'Selecione o produto.', type: 'warning' })
    return false
  }
  if ((form.origem === 'Fornecedor' || form.origem === 'Cliente') && !form.idParceiro) {
    emit('show-notification', { message: 'Selecione o parceiro.', type: 'warning' })
    return false
  }
  return true
}

// ============================================================
// SALVAR
// ============================================================
const confirmar = async () => {
  if (!validar()) return
  isSaving.value = true

  try {
    if (props.clausulaEditando) {
      await salvarEdicao()
    } else {
      await salvarCriacao()
    }
    emit('salva')
    emit('show-notification', { message: 'Cláusula salva com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar cláusula.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    else if (error?.message) msg = `Erro ao salvar cláusula: ${error.message}`
    console.error('[ClausulasModal] Erro ao salvar cláusula:', error)
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSaving.value = false
  }
}

// ============================================================
// SALVAR CRIAÇÃO
// ============================================================
const salvarCriacao = async () => {
  if (props.tipoDocumento === 'PO') {
    // PO usa endpoint especializado criar-com-origem
    await api.post(ep.value.criar, {
      idPurchaseOrder: props.idDocumento,
      titulo: form.titulo,
      texto: form.texto,
      origem: form.origem,
      idProduto: (form.origem === 'Produto' || form.origem === 'Produto Do Fornecedor') ? form.idProduto : null,
      idParceiro: (form.origem === 'Fornecedor') ? form.idParceiro : null,
      isDefinitiva: form.origem !== 'Avulsa' ? form.isDefinitiva : false,
      lstVisibilidade: form.lstVisibilidade || null,
      lstAplicacaoClausula: form.lstAplicacaoClausula || null
    })
  } else if (props.tipoDocumento === 'PV' && ep.value.criarComOrigem) {
    // PV usa endpoint especializado criar-com-origem
    await api.post(ep.value.criarComOrigem(props.idDocumento), {
      idPedidoVenda: props.idDocumento,
      titulo: form.titulo,
      texto: form.texto,
      origem: form.origem,
      idProduto: (form.origem === 'Produto' || form.origem === 'Produto Do Cliente') ? form.idProduto : null,
      idParceiro: (form.origem === 'Cliente') ? form.idParceiro : null,
      isDefinitiva: form.origem !== 'Avulsa' ? form.isDefinitiva : false,
      lstVisibilidade: form.lstVisibilidade || null,
      lstAplicacaoClausula: form.lstAplicacaoClausula || null
    })
  } else {
    // OC usa endpoint criar-com-origem (cria template + vínculo)
    await api.post(ep.value.criar(props.idDocumento), {
      idOrdemCompra: props.idDocumento,
      titulo: form.titulo,
      texto: form.texto,
      origem: form.origem,
      idProduto: (form.origem === 'Produto' || form.origem === 'Produto Do Fornecedor') ? form.idProduto : null,
      idParceiro: (form.origem === 'Fornecedor') ? form.idParceiro : null,
      isDefinitiva: form.origem !== 'Avulsa' ? form.isDefinitiva : false,
      lstVisibilidade: form.lstVisibilidade || null,
      lstAplicacaoClausula: form.lstAplicacaoClausula || null
    })
  }
}

// ============================================================
// SALVAR EDIÇÃO
// ============================================================
const salvarEdicao = async () => {
  const idClausula = props.clausulaEditando.idPurchaseOrderClausula ||
    props.clausulaEditando.idPedidoVendaClausula ||
    props.clausulaEditando.idOrdemCompraClausula

  const origemDescricao = props.clausulaEditando.origemDescricao || props.clausulaEditando.origem || ''

  // Verifica se a cláusula tem template FK (origem !== 'Avulsa' e !== tipoDocumento)
  const temTemplateFK = origemDescricao !== 'Avulsa' &&
    origemDescricao !== props.tipoDocumento

  if (temTemplateFK && ep.value.atualizarComTemplate && props.tipoDocumento === 'PO') {
    // PO com template: usa endpoint especializado
    await api.put(ep.value.atualizarComTemplate(idClausula), {
      texto: form.texto,
      atualizarTemplate: form.atualizarTemplate,
      xmin: form.xmin
    })
  } else {
    // Sem template FK ou PV/OC: PUT básico
    const payload = {
      titulo: form.titulo,
      texto: form.texto,
      ordemExibicao: props.clausulaEditando.ordemExibicao || 0,
      lstVisibilidade: form.lstVisibilidade || null,
      lstAplicacaoClausula: form.lstAplicacaoClausula || null,
      xmin: form.xmin
    }
    await api.put(ep.value.atualizarAvulsa(idClausula), payload)
  }
}

</script>

<template>
  <ModalFormularioV2
    :model-value="props.modelValue"
    :titulo="tituloModal"
    :loading="isSaving"
    tamanho="lg"
    @confirmar="confirmar"
    @update:modelValue="(v) => emit('update:modelValue', v)"
  >
    <div class="row g-3">
      <!-- MODO CRIAÇÃO -->
      <template v-if="!props.clausulaEditando">
        <!-- Origem -->
        <div class="col-12">
          <CampoSelect
            id="origem"
            label="Origem"
            v-model="form.origem"
            :options="opcoesOrigem"
            option-value="id"
            option-label="label"
            placeholder="Selecione a origem da cláusula..."
            required
          />
        </div>

        <!-- Campos que aparecem após selecionar origem -->
        <template v-if="form.origem">
          <div class="col-12">
            <CampoInput
              id="titulo"
              label="Título"
              v-model="form.titulo"
              required
              maxlength="200"
            />
          </div>
          <div class="col-12">
            <CampoInput
              id="texto"
              label="Texto da Cláusula"
              type="textarea"
              v-model="form.texto"
              required
            />
          </div>

          <!-- Produto (visível se origem = Produto, Produto Do Fornecedor ou Produto Do Cliente) -->
          <div v-if="form.origem === 'Produto' || form.origem === 'Produto Do Fornecedor' || form.origem === 'Produto Do Cliente'" class="col-md-6">
            <CampoSelect
              id="idProduto"
              label="Produto"
              v-model="form.idProduto"
              :options="props.produtosDisponiveis"
              option-value="id"
              option-label="label"
              placeholder="Selecione um produto..."
              required
            />
          </div>

          <!-- Parceiro (visível se origem = Fornecedor ou Cliente) -->
          <div v-if="form.origem === 'Fornecedor' || form.origem === 'Cliente'" class="col-md-6">
            <CampoSelect
              id="idParceiro"
              label="Parceiro"
              v-model="form.idParceiro"
              :options="props.parceirosDisponiveis"
              option-value="id"
              option-label="label"
              placeholder="Selecione um parceiro..."
              required
            />
          </div>

          <!-- Frequência (visível se origem !== Avulsa) -->
          <div v-if="form.origem !== 'Avulsa'" class="col-md-6">
            <CampoSelect
              id="isDefinitiva"
              label="Frequência"
              v-model="form.isDefinitiva"
              :options="[
                { id: true, label: 'Sempre (Definitiva)' },
                { id: false, label: 'Apenas nesta operação' }
              ]"
              option-value="id"
              option-label="label"
            />
          </div>

          <!-- LstVisibilidade -->
          <div class="col-md-6">
            <CampoBuscaLista
              id="lstVisibilidade"
              label="Visibilidade"
              v-model="form.lstVisibilidade"
              :tabela="'ClausulasComerciais'"
            />
          </div>

        </template>
      </template>

      <!-- MODO EDIÇÃO -->
      <template v-if="props.clausulaEditando">
        <div class="col-12">
          <CampoInput
            id="titulo"
            label="Título"
            v-model="form.titulo"
            required
            maxlength="200"
          />
        </div>
        <div class="col-12">
          <CampoInput
            id="texto"
            label="Texto da Cláusula"
            type="textarea"
            v-model="form.texto"
            required
          />
        </div>

        <!-- LstVisibilidade -->
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstVisibilidade"
            label="Visibilidade"
            v-model="form.lstVisibilidade"
            :tabela="'ClausulasComerciais'"
          />
        </div>

        <!-- Atualizar template original? (apenas se tiver template FK) -->
        <div
          v-if="props.clausulaEditando.origemDescricao !== 'Avulsa' && props.clausulaEditando.origemDescricao !== props.tipoDocumento"
          class="col-12"
        >
          <div class="alert alert-info py-2 mb-0">
            <CampoSelect
              id="atualizarTemplate"
              label="Atualizar template original?"
              v-model="form.atualizarTemplate"
              :options="[
                { id: true, label: 'Sim, atualizar para futuras operações' },
                { id: false, label: 'Não, alterar apenas neste documento' }
              ]"
              option-value="id"
              option-label="label"
            />
          </div>
        </div>
      </template>
    </div>
  </ModalFormularioV2>
</template>
